// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import * as StringUtil from 'app/common/lib/StringUtil';
import clickthroughImage from 'app/common/react/components/growth/activation/NuxChecklist/clickthroughImage.png';
import closeupImage from 'app/common/react/components/growth/activation/NuxChecklist/closeupImage.png';
import i18n from 'app/common/lib/i18n';
import NuxChecklistAction from 'app/common/react/components/growth/activation/NuxChecklist/NuxChecklistAction';
import NuxChecklistCelebration from 'app/common/react/components/growth/activation/NuxChecklist/NuxChecklistCelebration';
import ResourceFactory from 'app/common/lib/ResourceFactory';
import saveImage from 'app/common/react/components/growth/activation/NuxChecklist/saveImage.png';
import trackRegisterAction from 'app/common/lib/util/trackRegisterAction';
import type { Dispatch } from 'redux';
import type { State } from 'app/common/redux/types';
import { compose } from 'redux';
import { Experiment } from 'app/common/react/components/experiments';
import { HeaderHeight } from 'app/common/react/components/Header/Context/ContextConsumer';
import {
  getAndroidAndIosUrl,
  getAndroidAndIosAlt,
} from 'app/common/react/components/growth/activation/AppUpsellHero/AppUpsellHero';
import {
  withRequestContext,
  type RequestContextType,
} from 'app/common/react/components/RequestContext';
import { Box, Column, Heading, Sticky, IconButton, Text, Link } from 'gestalt';

type NuxChecklistProps = {|
  experienceId: number,
  checklistExpGroup: string,
  completeAppInstall: () => void,
  handleComplete: () => void,
  handleDismiss: () => void,
  initialDidPinClickthrough: boolean,
  initialDidPinCloseup: boolean,
  initialDidPinSave: boolean,
  initialDidAppInstall: boolean,
  didPinClickthrough: boolean,
  didPinCloseup: boolean,
  didPinSave: boolean,
  didAppInstall: boolean,
  updatePinActions: (payload: {
    didPinClickthrough: boolean,
    didPinCloseup: boolean,
    didPinSave: boolean,
    didAppInstall: boolean,
  }) => void,
  requestContext?: RequestContextType,
  firstName: string,
|};

type NuxChecklistState = {|
  showCelebration: boolean,
  fadeChecklistOut: boolean,
  showAppInstallButtons: boolean,
|};

const fadeOutStyles = () => ({
  __style: {
    transition: 'opacity 0.4s ease',
    opacity: 0,
    backgroundColor: 'white',
  },
});

const CHECKLIST_MAX_WIDTH = 180,
  CHECKLIST_CONTAINER_MAX_WIDTH = 550,
  CHECKLIST_RIGHT_SHAPE_MAX_WIDTH = 147,
  CHECKLIST_ACTIONS_MAX_WIDTH = 900;

/* eslint-disable max-len */
const leftShape = (
  <svg width="100%" viewBox="0 0 180 216" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M30.8631 205.998C59.1975 205.996 62 164 115.975 182C183.108 204.388 177.698 116.569 179.299 99.9947C185.428 36.5372 135 0 135 0L-1.52588e-05 0C-1.52588e-05 0 -1.52588e-05 58.9599 -1.52588e-05 86.9599C-1.52588e-05 119.921 -1.52588e-05 139.298 -1.52588e-05 160.523C-1.52588e-05 191.01 1.99998 206 30.8631 205.998Z"
      fill="#FF4C57"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M155.947 115.187C155.947 115.187 143.265 114.298 114.742 114.272C86.2189 114.247 52.4604 135.416 53.9576 157.53C55.4547 179.644 81.8162 213.212 81.8162 213.212L127.848 150.451L155.947 115.187Z"
      fill="#FFA8D9"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M31.0349 46.563C31.0349 46.563 22.0026 92.3392 5.23879 106.482C-2.59047 113.087 0.0740929 106.742 0.0740945 100.904C0.074094 91.9552 2.1878e-05 89.7371 2.06745e-05 86.9599C2.16297e-05 71.3676 0.0740938 53.9269 2.06478e-05 45.6876C-0.0101528 44.5558 -0.0696632 41.6699 2.14539e-05 40.2255C0.0289433 39.626 -0.144271 39.3266 0.0740932 39.4217C7.812 42.7917 31.0349 46.563 31.0349 46.563Z"
      fill="#007AFF"
    />
  </svg>
);

const rightShape = (
  <svg width="100%" viewBox="0 0 147 183">
    <g id="Page-1" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
      <g id="greenblue-blob">
        <path
          d="M147,0.530212 C118.953,2.331 90.559,9.06317 89.861,9.40159 C65.183,21.3691 30.9294,108.148 30.9294,108.148 C30.9294,108.148 65.112,163.464 111.011,179.085 C132.646,186.449 139.394,179.645 146.741,172.238 C146.827,172.151 146.913,172.064 147,171.977 L147,0.530212 Z"
          id="Path"
          fill="#007A65"
        />
        <path
          d="M0.2356,60.1427 L21.1035,14.114 C21.1035,14.114 50.222,61.7981 69.048,77.0664 C87.875,92.3346 118.715,106.829 118.715,106.829 L67.593,172.262 L0.2356,60.1427 Z"
          id="Path"
          fill="#007AFF"
        />
      </g>
    </g>
  </svg>
);
/* eslint-enable max-len */

// $FlowFixMe Flow v0.104.0 upgrade supression.
export function getUserStatePinActions(userId: string): Promise<*> {
  return new Promise((resolve, reject) =>
    ResourceFactory.create('UserResource', {
      user_id: userId,
      field_set_key: 'pin_actions',
    })
      .callGet()
      .then(resolve)
      .catch(reject),
  );
}

export function completeUserStatePinAction(
  state: 'HAS_DONE_CLICKTHROUGH' | 'HAS_DONE_CLOSEUP' | 'HAS_DONE_APP_INSTALL',
): Promise<any> {
  return new Promise((resolve, reject) =>
    ResourceFactory.create('UserStateResource', { state, value: 1 })
      .callCreate()
      .then(resolve)
      .catch(reject),
  );
}

export class NuxChecklist extends React.PureComponent<NuxChecklistProps, NuxChecklistState> {
  constructor(props: NuxChecklistProps) {
    super(props);
    this.state = {
      showCelebration: false,
      fadeChecklistOut: false,
      showAppInstallButtons: false,
    };
    const {
      initialDidPinClickthrough,
      initialDidPinCloseup,
      initialDidPinSave,
      initialDidAppInstall,
      updatePinActions,
    } = props;
    let { didPinClickthrough, didPinCloseup, didPinSave, didAppInstall } = props;

    // initial pin actions from server-side are more accurate on page reload
    // ex of when this isn't true: new user does not initially have closeup completed
    // they click on closeup and click back (this is not a full page reload)
    // redux store info is more accurate in this case
    if (initialDidPinClickthrough) {
      didPinClickthrough = true;
    }
    if (initialDidPinCloseup) {
      didPinCloseup = true;
    }
    if (initialDidPinSave) {
      didPinSave = true;
    }
    if (initialDidAppInstall) {
      didAppInstall = true;
    }

    updatePinActions({ didPinClickthrough, didPinCloseup, didPinSave, didAppInstall });
  }

  startCelebrationAnimation() {
    setTimeout(() => {
      window.requestAnimationFrame(() => {
        this.setState({ fadeChecklistOut: true });
        window.requestAnimationFrame(() => {
          this.setState({ showCelebration: true });
        });
      });
    }, this.celebrationAnimationDelayMS);
  }

  componentDidMount() {
    const {
      experienceId,
      handleComplete,
      initialDidPinClickthrough,
      initialDidPinCloseup,
      initialDidPinSave,
      initialDidAppInstall,
      checklistExpGroup,
    } = this.props;
    let { didPinClickthrough, didPinCloseup, didPinSave, didAppInstall } = this.props;

    if (initialDidPinClickthrough) {
      didPinClickthrough = true;
    }
    if (initialDidPinCloseup) {
      didPinCloseup = true;
    }
    if (initialDidPinSave) {
      didPinSave = true;
    }
    if (initialDidAppInstall) {
      didAppInstall = true;
    }

    const allActionsCompleted =
      (experienceId === PCONST.thrift.Experience.WEB_CHECKLIST_V2
        ? didAppInstall
        : didPinClickthrough) &&
      didPinCloseup &&
      didPinSave;
    if (allActionsCompleted) {
      const inCelebrationAnimationExp =
        checklistExpGroup === 'check_celebration_animation' ||
        checklistExpGroup === 'celebration_animation' ||
        checklistExpGroup === 'enabled';
      if (inCelebrationAnimationExp) {
        this.startCelebrationAnimation();
      } else {
        setTimeout(handleComplete, this.delayBeforeCompletionMS);
      }
    }
  }

  delayBeforeCompletionMS = 5000;
  celebrationAnimationDelayMS = 3500;

  completeAppInstall = () => {
    const { didAppInstall, completeAppInstall } = this.props;

    if (!didAppInstall) {
      completeUserStatePinAction('HAS_DONE_APP_INSTALL');
      completeAppInstall();
    }
  };

  render() {
    const {
      experienceId,
      didPinClickthrough,
      didPinCloseup,
      didPinSave,
      didAppInstall,
      handleDismiss,
      checklistExpGroup,
      handleComplete,
      firstName,
      requestContext,
    } = this.props;
    const { showCelebration, fadeChecklistOut, showAppInstallButtons } = this.state;
    const inCheckAnimationExp =
      checklistExpGroup === 'check_animation' ||
      checklistExpGroup === 'check_celebration_animation' ||
      checklistExpGroup === 'enabled';

    const isChecklistV2 = experienceId === PCONST.thrift.Experience.WEB_CHECKLIST_V2;

    let headingText;
    const allActionsCompleted =
      (isChecklistV2 ? didAppInstall : didPinClickthrough) && didPinCloseup && didPinSave;

    if (isChecklistV2) {
      headingText = i18n._(
        "Here's how to get the most out of Pinterest",
        'user has a list of actions below the header to complete',
      );
    } else if (allActionsCompleted) {
      headingText = i18n._(
        "You're ready! Keep exploring to find your next great idea",
        'user completed list of actions below the header',
      );
    } else if (didPinSave) {
      headingText = i18n._(
        'Saved! Each Pin you save improves your feed',
        'user completed a pin save and the header text is changing',
      );
    } else if (didPinClickthrough) {
      headingText = i18n._(
        'See! Every Pin links to the website it came from',
        'user completed a pin clickthrough and the header text is changing',
      );
    } else if (didAppInstall) {
      headingText = i18n._(
        'Nice! The app offers a faster Pinterest experience on the go',
        'user has a todo checklist item to install the mobile app from the google play or apple store',
      );
    } else if (didPinCloseup) {
      headingText = i18n._(
        'Nice find! You can click any Pin to learn more about it',
        'user completed a closeup and the header text is changing',
      );
    } else {
      headingText = i18n._(
        "Here's how to get the most out of Pinterest",
        'user has a list of actions below the header to complete',
      );
    }

    const checklistActions = [
      {
        name: 'closeup',
        accessibilityLabel: 'closeup',
        icon: 'arrow-up-right',
        text: i18n._(
          'Click on a Pin you like',
          'explaining what to do to complete the closeup step of checklist',
        ),
        isCompleted: didPinCloseup,
        image: closeupImage,
      },
      {
        name: 'repin',
        accessibilityLabel: 'save',
        icon: 'pin',
        text: i18n._(
          'Save it for later',
          'explaining what to do to complete the pin save step of checklist',
        ),
        isCompleted: didPinSave,
        image: saveImage,
      },
      isChecklistV2
        ? {
            name: 'appInstall',
            accessibilityLabel: 'link',
            icon: 'download',
            text: i18n._(
              'Get the app',
              'explaining what to do to complete the app install step of checklist',
            ),
            isCompleted: didAppInstall,
            image: '',
          }
        : {
            name: 'clickthrough',
            accessibilityLabel: 'link',
            icon: 'globe',
            text: i18n._(
              "Visit the link it's from ",
              'explaining what to do to complete the clickthrough step of checklist',
            ),
            isCompleted: didPinClickthrough,
            image: clickthroughImage,
          },
    ];

    let lang;

    const language = requestContext && requestContext.language;
    if (language) {
      const locale = requestContext && requestContext.locale;
      lang = language.startsWith('zh') ? locale : language;
      if (lang) {
        lang = lang.toUpperCase();
      }
    }

    const { iosUrl, androidUrl } = getAndroidAndIosUrl(lang);
    const { iosAlt, androidAlt } = getAndroidAndIosAlt();

    return (
      <HeaderHeight>
        {(headerHeight) => (
          <Sticky dangerouslySetZIndex={{ __zIndex: 2 }} top={headerHeight}>
            {!showCelebration ? (
              <Box dangerouslySetInlineStyle={fadeChecklistOut ? fadeOutStyles() : null}>
                <Box
                  color={!isChecklistV2 && 'lightGray'}
                  display="flex"
                  dangerouslySetInlineStyle={{
                    __style: isChecklistV2
                      ? { backgroundColor: '#BCE2EE' }
                      : { overflow: 'hidden' },
                  }}
                >
                  {isChecklistV2 && (
                    <Box
                      mdDisplay="none"
                      lgDisplay="block"
                      width="100%"
                      maxWidth={CHECKLIST_MAX_WIDTH}
                    >
                      {leftShape}
                    </Box>
                  )}
                  <Box
                    display="flex"
                    direction="column"
                    alignItems={!isChecklistV2 && 'center'}
                    justifyContent="center"
                    paddingX={12}
                    paddingY={8}
                    color={isChecklistV2 ? 'transparent' : 'blue'}
                    maxWidth={CHECKLIST_CONTAINER_MAX_WIDTH}
                    width="100%"
                    dangerouslySetInlineStyle={{ __style: { borderTopRightRadius: '80px 380px' } }}
                  >
                    {isChecklistV2 && (
                      <Text size="xl">
                        {StringUtil.dangerouslyFormat(
                          i18n._(
                            'Welcome, {{ firstName }}!',
                            'welcome user by first name in a banner on homefeed',
                          ),
                          {
                            firstName,
                          },
                        )}
                      </Text>
                    )}
                    <Heading
                      size="sm"
                      color={isChecklistV2 ? 'darkGray' : 'white'}
                      overflow="normal"
                    >
                      {headingText}
                    </Heading>
                  </Box>
                  <Box position="absolute" top right paddingY={2} paddingX={5}>
                    <IconButton
                      accessibilityLabel={i18n._(
                        'Hide Checklist',
                        'hide the checklist of pin actions at the top of the homefeed',
                      )}
                      icon="cancel"
                      onClick={handleDismiss}
                      size="sm"
                      iconColor={isChecklistV2 ? 'white' : 'gray'}
                    />
                  </Box>
                  <Box width="100%" display="flex" alignItems="center" justifyContent="center">
                    <Box
                      color={!isChecklistV2 && 'lightGray'}
                      display="flex"
                      dangerouslySetInlineStyle={{
                        __style: isChecklistV2
                          ? { backgroundColor: '#BCE2EE' }
                          : { overflow: 'hidden' },
                      }}
                    >
                      <Box
                        mdDisplay="none"
                        lgDisplay="block"
                        width="100%"
                        maxWidth={CHECKLIST_MAX_WIDTH}
                      >
                        {leftShape}
                      </Box>
                      <Box
                        display="flex"
                        direction="column"
                        alignItems={!isChecklistV2 && 'center'}
                        justifyContent="center"
                        paddingX={12}
                        paddingY={8}
                        color={isChecklistV2 ? 'transparent' : 'blue'}
                        maxWidth={CHECKLIST_CONTAINER_MAX_WIDTH}
                        width="100%"
                        dangerouslySetInlineStyle={{
                          __style: { borderTopRightRadius: '80px 380px' },
                        }}
                      >
                        {isChecklistV2 && (
                          <Text size="xl">
                            {StringUtil.dangerouslyFormat(
                              i18n._(
                                'Welcome, {{ firstName }}!',
                                'welcome user by first name in a banner on homefeed',
                              ),
                              {
                                firstName,
                              },
                            )}
                          </Text>
                        )}
                        <Heading
                          size="sm"
                          color={isChecklistV2 ? 'darkGray' : 'white'}
                          overflow="normal"
                        >
                          {headingText}
                        </Heading>
                      </Box>
                      <Box position="absolute" top right paddingY={2} paddingX={5}>
                        <IconButton
                          accessibilityLabel={i18n._(
                            'Hide Checklist',
                            'hide the checklist of pin actions at the top of the homefeed',
                          )}
                          icon="cancel"
                          onClick={handleDismiss}
                          size="sm"
                          iconColor={isChecklistV2 ? 'white' : 'gray'}
                        />
                      </Box>
                      <Box width="100%" display="flex" alignItems="center" justifyContent="center">
                        <Box
                          display="flex"
                          paddingX={!isChecklistV2 ? 4 : 0}
                          paddingY={4}
                          marginStart={!isChecklistV2 && -4}
                          marginEnd={!isChecklistV2 && -4}
                          marginTop={!isChecklistV2 ? -2 : 0}
                          marginBottom={!isChecklistV2 ? -2 : 0}
                          maxWidth={CHECKLIST_ACTIONS_MAX_WIDTH}
                          width="100%"
                          wrap
                          dangerouslySetInlineStyle={{
                            __style: isChecklistV2 && { backgroundColor: '#BCE2EE' },
                          }}
                        >
                          {checklistActions.map(
                            (
                              { name, icon, accessibilityLabel, text, isCompleted, image },
                              idx: number,
                            ) => {
                              const isAppInstall = name === 'appInstall';

                              return (
                                <Column key={text} lgSpan={isChecklistV2 ? 12 : 4} span={12}>
                                  <Box
                                    display="flex"
                                    justifyContent="center"
                                    paddingX={4}
                                    paddingY={2}
                                  >
                                    {/* eslint-disable jsx-a11y/click-events-have-key-events */}
                                    {/* eslint-disable jsx-a11y/no-static-element-interactions */}
                                    <div
                                      onClick={() => trackRegisterAction(`checklist.${name}.click`)}
                                    >
                                      <NuxChecklistAction
                                        idx={idx}
                                        accessibilityLabel={accessibilityLabel}
                                        icon={icon}
                                        isCompleted={isCompleted}
                                        inCheckAnimationExp={inCheckAnimationExp}
                                        text={text}
                                        image={image}
                                        isChecklistV2={isChecklistV2}
                                        noVerticalCenter={isAppInstall && showAppInstallButtons}
                                      >
                                        <Box>
                                          <Box display="flex" alignItems="center">
                                            <Text size="xl">{text}</Text>
                                            {isAppInstall && (
                                              <IconButton
                                                accessibilityLabel={i18n._(
                                                  'Show app store install buttons',
                                                  'Toggle to show ios and android app store installs',
                                                )}
                                                icon={
                                                  showAppInstallButtons
                                                    ? 'arrow-down'
                                                    : 'arrow-forward'
                                                }
                                                onClick={() =>
                                                  this.setState(({ showAppInstallButtons }) => ({
                                                    showAppInstallButtons: !showAppInstallButtons,
                                                  }))
                                                }
                                                iconColor="darkGray"
                                                size="xs"
                                              />
                                            )}
                                          </Box>
                                          {isAppInstall &&
                                            showAppInstallButtons && (
                                              <Box display="flex" marginTop={4}>
                                                <Box marginEnd={2}>
                                                  <Link
                                                    href="https://bnc.lt/unauth-android-upsell-desktop-web"
                                                    target="blank"
                                                    inline
                                                    onClick={this.completeAppInstall}
                                                  >
                                                    <img
                                                      style={{ width: 104, height: 31 }}
                                                      src={androidUrl}
                                                      alt={androidAlt}
                                                    />
                                                  </Link>
                                                </Box>
                                                <Link
                                                  href="https://bnc.lt/unauth-ios-upsell-desktop-web"
                                                  target="blank"
                                                  inline
                                                  onClick={this.completeAppInstall}
                                                >
                                                  <img
                                                    style={{ width: 104, height: 31 }}
                                                    src={iosUrl}
                                                    alt={iosAlt}
                                                  />
                                                </Link>
                                              </Box>
                                            )}
                                        </Box>
                                      </NuxChecklistAction>
                                    </div>
                                    {/* eslint-enable jsx-a11y/click-events-have-key-events */}
                                    {/* eslint-enable jsx-a11y/no-static-element-interactions */}
                                  </Box>
                                </Column>
                              );
                            },
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                  {isChecklistV2 && (
                    <Box
                      mdDisplay="none"
                      lgDisplay="block"
                      width="100%"
                      maxWidth={CHECKLIST_RIGHT_SHAPE_MAX_WIDTH}
                    >
                      {rightShape}
                    </Box>
                  )}
                </Box>
              </Box>
            ) : (
              <NuxChecklistCelebration handleComplete={handleComplete} />
            )}
          </Sticky>
        )}
      </HeaderHeight>
    );
  }
}

const mapDispatchToProps = (dispatch: Dispatch<any>) => ({
  updatePinActions: (payload) => dispatch({ type: 'UPDATE_PIN_ACTIONS', payload }),
  completeAppInstall: () => dispatch({ type: 'COMPLETE_APP_INSTALL' }),
});

const mapStateToProps = ({
  ui: {
    checklist: { didPinCloseup, didPinClickthrough, didPinSave, didAppInstall },
  },
}: State) => ({ didPinCloseup, didPinClickthrough, didPinSave, didAppInstall });

const ChecklistWithExperiments = (props: NuxChecklistProps) => (
  <Experiment name="web_checklist_check_celebration_animation">
    {({ group }) => <NuxChecklist {...props} checklistExpGroup={group} />}
  </Experiment>
);

export default compose(
  // $FlowFixMe: new errors found from flow 0.96 upgrade
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withRequestContext,
)(ChecklistWithExperiments);
